import { useEffect, useRef, useState } from 'react';
import type { ServerEvent } from '@leadgab/shared';
import { useRealtimeEvent } from './realtime.tsx';
import { setAlertTitle } from './title.ts';

/**
 * Blinks the browser tab while a customer is waiting and nobody is looking.
 *
 * An agent with twenty tabs open does not watch this one. The title alternates
 * rather than showing a static count, because a number that simply sits there
 * is invisible in peripheral vision — movement is the whole signal. The
 * favicon gets a dot at the same time, since a narrow tab shows the icon and
 * two words of the title and nothing else.
 *
 * It stops the moment the window is looked at, and puts the title and the icon
 * back exactly as they were.
 */

/** Slow enough not to be frantic, fast enough to catch the eye. */
const BLINK_MS = 1200;

/**
 * Whether this event is a customer writing in.
 *
 * The same rule the widget and the conversation page use: no author and not a
 * system notice. An agent's own reply, a welcome, an auto-response and a stock
 * alert all arrive on this socket too, and blinking the tab at an agent for
 * something the software just said on their behalf would be noise they learn
 * to ignore — after which the signal is worth nothing.
 */
function fromCustomer(event: ServerEvent): boolean {
  if (event.type !== 'message') return false;

  const message = event.message as { user?: { id: number | null }; meta?: { system?: boolean } };

  return message.user?.id === null && !message.meta?.system;
}

/** Focus, not visibility: a tab can be `visible` inside a window behind Slack. */
function beingWatched(): boolean {
  return document.visibilityState === 'visible' && document.hasFocus();
}

export function useTabAlert(): void {
  // Conversations that have written while the agent was away — a Set, so five
  // messages from one person is one person waiting, which is what every other
  // count in this app means.
  const [waiting, setWaiting] = useState<string[]>([]);
  const waitingRef = useRef(waiting);
  waitingRef.current = waiting;

  useRealtimeEvent((event) => {
    if (!fromCustomer(event) || beingWatched()) return;

    const uuid = (event as { message: { sessionUuid?: string } }).message.sessionUuid ?? '';

    setWaiting((previous) => (previous.includes(uuid) ? previous : [...previous, uuid]));
  });

  // Clear as soon as they come back. `focus` covers alt-tabbing to the window;
  // `visibilitychange` covers switching to this tab within it. Neither implies
  // the other, so both are needed.
  useEffect(() => {
    const clear = (): void => {
      if (!beingWatched()) return;
      if (waitingRef.current.length > 0) setWaiting([]);
    };

    window.addEventListener('focus', clear);
    document.addEventListener('visibilitychange', clear);

    return () => {
      window.removeEventListener('focus', clear);
      document.removeEventListener('visibilitychange', clear);
    };
  }, []);

  // The blink itself.
  useEffect(() => {
    if (waiting.length === 0) return;

    const alert =
      waiting.length === 1 ? '● New message' : `● ${String(waiting.length)} new messages`;

    let showing = false;

    const timer = window.setInterval(() => {
      showing = !showing;
      // Not `document.title = …`: the page open underneath has a title of its
      // own, and whichever of us wrote last would win. Clearing the layer lets
      // the page's title show through for the dark half of the blink, and
      // clearing it for good at the end needs no memory of what was there.
      setAlertTitle(showing ? alert : null);
    }, BLINK_MS);

    // Start on the alert rather than waiting out the first interval, so the tab
    // moves at the moment the message lands.
    setAlertTitle(alert);
    showing = true;

    const restoreIcon = markFavicon();

    return () => {
      window.clearInterval(timer);
      setAlertTitle(null);
      restoreIcon();
    };
  }, [waiting]);
}

/**
 * Paints a dot on the favicon, and returns the undo.
 *
 * Drawn over the real icon rather than swapping in a second file, so there is
 * one icon to keep in step. If anything about that fails — the image is
 * missing, the canvas is unavailable — the dot is skipped and the title blink
 * carries the signal on its own.
 */
function markFavicon(): () => void {
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"][sizes="32x32"]');
  if (!link) return () => undefined;

  const original = link.href;
  let cancelled = false;

  const image = new Image();
  image.src = original;

  image.onload = () => {
    if (cancelled) return;

    try {
      const canvas = document.createElement('canvas');
      canvas.width = 32;
      canvas.height = 32;

      const context = canvas.getContext('2d');
      if (!context) return;

      context.drawImage(image, 0, 0, 32, 32);

      // Top right, with a ring in the page background colour so the dot reads
      // against a dark icon as well as a light one.
      context.beginPath();
      context.arc(23, 9, 9, 0, Math.PI * 2);
      context.fillStyle = '#ffffff';
      context.fill();

      context.beginPath();
      context.arc(23, 9, 7, 0, Math.PI * 2);
      context.fillStyle = '#e11d48';
      context.fill();

      link.href = canvas.toDataURL('image/png');
    } catch {
      // A tainted or unavailable canvas is not worth breaking the tab over.
    }
  };

  return () => {
    cancelled = true;
    link.href = original;
  };
}
